import { progressIndicator } from 'root/main/components/progressIndicator';
import { formIsCorrectForPostalCodeTest } from 'root/helpers/formHelpers';
import {
    getDecodedFormFieldValue,
    removeFormFieldValue,
} from 'root/utils/dom/localStorage';
import { handleSSBORedirectClick } from 'root/core/tracking/ssboRedirectTracking';

class Wizard {
    #wizard: HTMLElement;
    #wizardStepVariations: string[]; // With wizardStepVariations we track which "variation" of the step the user has chosen, so that they'll see it again if they use the back and forward buttons. So if they've chosen 'retail' as the second step, then the second item in the array will be 'retail'. The first element of the array is always the 'default' step they start on. So ['default', 'retail'] shows that the first step is default, and the second is retail.
    #currentStep: number;
    #visiblePanel: HTMLElement | null;
    #disabledSteps: Set<string>;
    #resetPosition: () => void;

    constructor(el: HTMLElement) {
        this.#wizard = el;
        this.#wizardStepVariations = ['default'];
        this.#currentStep = 0;
        this.#visiblePanel = null;
        this.#disabledSteps = new Set();
        this.#resetPosition = this.triggerResetAfterInitialPanelLoads(this.#wizard);
    }

    triggerResetAfterInitialPanelLoads(wizard: HTMLElement) {
        // Function resets the position of the wizard panel after the initial panel has loaded.
        let isAfterInitialLoad = false;
        function resetPosition() {
            if (isAfterInitialLoad) {
                wizard.scrollIntoView(true);
            }
            isAfterInitialLoad = true;
        }
        return resetPosition;
    }

    #hidePanel(panel: HTMLElement) {
        panel.dataset.wizardShowPanel = 'false';
    }

    #showPanel(panel: HTMLElement, focusOnFirstElement: boolean) {
        this.#resetPosition();
        this.#wizard.style.height = `${panel.scrollHeight}px`;
        const form = this.#wizard.closest('form');
        panel.dataset.wizardShowPanel = 'true';

        if (focusOnFirstElement === true) {
            panel.focus();
        }

        // if it's the last step and formIsCorrectForPostalCodeTest, push to VWO
        const panels: HTMLElement[] = Array.from(
            this.#wizard.querySelectorAll('[data-wizard-panel]')
        );
        const lastStep = panels.reduce(
            (highestStep, panel) =>
                Math.max(highestStep, Number(panel.dataset.wizardPanelStep)),
            0
        );
        // @todo MW-9674: Remove or refactor after postal code DE test concludes
        if (form) {
            if (this.#currentStep === lastStep && formIsCorrectForPostalCodeTest(form)) {
                document.dispatchEvent(new CustomEvent('reached_form_step'));

                // keep this part to test using "goals" for reports
                (window as any).VWO = (window as any).VWO || [];
                (window as any).VWO.event =
                    (window as any).VWO.event ||
                    function (...args: unknown[]) {
                        ((window as any).VWO.event.q =
                            (window as any).VWO.event.q || []).push(args);
                    };
                (window as any).VWO.event('reached_form_step');
            }
        }
    }
    #potentiallyHideNextButton(panel: HTMLElement) {
        const nextStepVariation =
            this.#wizardStepVariations[this.#getNewStep(this.#currentStep, 'next')];
        const nextButton = panel.querySelector('[data-wizard-next-button]');
        const selectedButton = panel.querySelector<HTMLElement>(
            '[data-wizard-button-selected="true"]'
        );

        if (
            this.isSubPanel('hidden') &&
            selectedButton &&
            'wizardSubPanelButton' in selectedButton.dataset
        ) {
            nextButton?.classList.remove('gb-hide');
            return;
        }

        if (nextButton) {
            nextButton.classList.toggle('gb-hide', !nextStepVariation);
        }
    }
    #potentiallyHidePreviousButton(panel: HTMLElement) {
        const previousButton = panel.querySelector('[data-wizard-go-previous]');
        if (this.isSubPanel('visible')) {
            previousButton?.classList.remove('gb-hide');
            return;
        }
        if (
            previousButton &&
            (this.#currentStep === 0 ||
                !this.#wizardStepVariations[
                    this.#getNewStep(this.#currentStep, 'previous')
                ])
        ) {
            previousButton.classList.add('gb-hide');
        }
    }
    #setCurrentStep(step: number) {
        this.#currentStep = step;
        this.showCurrentState();
    }
    #getAdjacentSelectionButtons(button: HTMLButtonElement) {
        const panelGridContainerEl = button?.closest(
            '[data-wizard-panel] .wizard__panel-grid'
        );
        return panelGridContainerEl
            ? (Array.from(
                  panelGridContainerEl.querySelectorAll(
                      '[data-wizard-selection-button], [data-wizard-sub-panel-button]'
                  )
              ).filter(
                  adjacentButton => adjacentButton !== button
              ) as HTMLButtonElement[])
            : [];
    }
    #handleSettingSelectionButtons(e: Event) {
        // Set selected state of buttons for the current panel
        if (e.currentTarget instanceof HTMLButtonElement) {
            const adjacentSelectionButtons = this.#getAdjacentSelectionButtons(
                e.currentTarget
            );
            if (adjacentSelectionButtons) {
                adjacentSelectionButtons.forEach(button => {
                    button.dataset.wizardButtonSelected = 'false';
                });
                e.currentTarget.dataset.wizardButtonSelected = 'true';
            }
        }
    }
    #getNewStep(step: number, direction: 'next' | 'previous'): number {
        const stepIncrement = direction === 'next' ? 1 : -1;
        let newStep = step + stepIncrement;
        if (isNaN(newStep) || newStep < 0) newStep = 0;
        // All panels of the same step should have the same slug, so we just find the first
        const newStepSlug = Array.from(
            this.#wizard.querySelectorAll<HTMLElement>('[data-wizard-panel]')
        ).find(panel => Number(panel.dataset.wizardPanelStep) === newStep)?.dataset
            .wizardPanelVariation;
        return newStepSlug && this.#disabledSteps.has(newStepSlug)
            ? this.#getNewStep(newStep, direction)
            : newStep;
    }
    #handleStepDisabling(selectionButton: HTMLButtonElement) {
        // Previously only used for number_of_locations, now it is unused entirely but remains for future use
        // First, re-enable any steps that may have been disabled by a button on this current step
        const adjacentSelectionButtons =
            this.#getAdjacentSelectionButtons(selectionButton);
        for (const adjacentSelectionButton of adjacentSelectionButtons) {
            const stepToReenable = adjacentSelectionButton.dataset.wizardDisableStep;
            if (stepToReenable) {
                this.#disabledSteps.delete(stepToReenable);
            }
        }
        // Then disable a new step if needed
        const stepToDisable = selectionButton.dataset.wizardDisableStep;
        if (stepToDisable) {
            this.#disabledSteps.add(stepToDisable);
            if (getDecodedFormFieldValue(stepToDisable)) {
                removeFormFieldValue(stepToDisable);
            }
        }
    }

    #setFullPageHeightOnMobile(panels: HTMLElement[]) {
        const isMobile = window.innerWidth < 960;
        if (isMobile) {
            const wrapper: HTMLElement | null = document.querySelector(
                '[data-wizard-wrapper-full-page]'
            );
            // get the height of the longest panel
            const panelHeights = panels.map(
                panel => panel?.getBoundingClientRect().height
            );
            const longestHeightOfPanels: number = Math.max(...panelHeights);

            // assign that height to the wrapper
            wrapper &&
                (wrapper.style.height = `${Math.round(longestHeightOfPanels) + 122}px`); // the 120px is to account from margin created on the wizard__panel-main-content to position the progress indicator
        }
    }
    showCurrentState(focusOnFirstElement = true) {
        const currentStepVariation = this.#wizardStepVariations[this.#currentStep];
        const panels: HTMLElement[] = Array.from(
            this.#wizard.querySelectorAll('[data-wizard-panel]')
        );
        panels.forEach(panel => {
            if (
                currentStepVariation === panel.dataset.wizardPanelVariation &&
                this.#currentStep === Number(panel.dataset.wizardPanelStep)
            ) {
                this.#showPanel(panel, focusOnFirstElement);
                this.#visiblePanel = panel;
                this.#potentiallyHideNextButton(panel);
                this.#potentiallyHidePreviousButton(panel);
            } else {
                this.#hidePanel(panel);
            }
        });

        const timelineContainer = this.#wizard.closest<HTMLElement>(
            '[data-wizard=timeline-container]'
        );
        if (timelineContainer) {
            progressIndicator(
                timelineContainer,
                this.#currentStep
            ).syncProgressIndicatorToStep();
        }
        this.#setFullPageHeightOnMobile(panels);
    }

    handleConsentPanelPreviousButton() {
        const wizardFormContainer =
            this.#wizard.closest<HTMLElement>('[data-wizard-form]');
        const progressIndicatorContainer =
            wizardFormContainer?.querySelector<HTMLElement>('[data-progress-indicator]');
        const businessTypePanel = wizardFormContainer?.querySelector<HTMLElement>(
            '[data-wizard-panel-slug="business_type"]'
        );
        // unhide progress indicator when returning back to regular lightbox panel flow
        if (progressIndicatorContainer)
            progressIndicatorContainer.style.display = 'block';
        const businessTypePanelStepNumber = Number(
            businessTypePanel?.dataset.wizardPanelStep
        );
        // go back to the business type panel
        this.#setCurrentStep(businessTypePanelStepNumber);
        // hide the sub panel on the business type panel (must happen after setting current step)
        this.setSubPanelVisibility('hidden');
        // explicitly hide the previous button on the panel if it is the first step
        if (this.#visiblePanel?.dataset.wizardPanelStep === '0') {
            const previousButton = this.#visiblePanel.querySelector(
                '[data-wizard-go-previous]'
            );
            previousButton?.classList.add('gb-hide');
        }
        return;
    }

    isSubPanel(status: 'visible' | 'hidden'): boolean {
        if (this.#visiblePanel && 'wizardSubPanel' in this.#visiblePanel.dataset) {
            return this.#visiblePanel.dataset.wizardSubPanel === status;
        }
        return false;
    }
    setSubPanelVisibility(visible: 'visible' | 'hidden') {
        if (this.#visiblePanel && 'wizardSubPanel' in this.#visiblePanel.dataset) {
            this.#visiblePanel.dataset.wizardSubPanel = visible;
            const allPanelGridAndHeaderEls = this.#visiblePanel.querySelectorAll(
                '.wizard__panel__header, .wizard__panel-grid, .wizard__sub-panel__header, .wizard__sub-panel-grid'
            );
            allPanelGridAndHeaderEls.forEach(el => {
                el.classList.toggle('tw:animate-fade-in-fast');
                el.classList.toggle('tw:animate-fade-out-fast');
            });
        }
    }

    listenToButtons() {
        const nextButtons: HTMLButtonElement[] = Array.from(
            this.#wizard.querySelectorAll('[data-wizard-go-next]')
        );
        nextButtons.forEach(nextButton => {
            nextButton.addEventListener('click', () => {
                const panelEl = nextButton.closest<HTMLElement>('[data-wizard-panel]');
                const selectedButton = panelEl?.querySelector<HTMLElement>(
                    '[data-wizard-button-selected="true"]'
                );

                if (
                    selectedButton &&
                    panelEl &&
                    'wizardSubPanelButton' in selectedButton.dataset &&
                    this.isSubPanel('hidden')
                ) {
                    this.setSubPanelVisibility('visible');
                    this.#potentiallyHidePreviousButton(panelEl);
                    this.#potentiallyHideNextButton(panelEl);
                    return;
                }

                const newStep = this.#getNewStep(this.#currentStep, 'next');
                const variation = nextButton.dataset.wizardSetStepVariation;
                if (variation) {
                    this.#wizardStepVariations[newStep] = variation;
                }
                this.#setCurrentStep(newStep);
            });
        });
        const previousButtons = this.#wizard.querySelectorAll(
            '[data-wizard-go-previous]'
        );
        previousButtons.forEach(previousButton => {
            previousButton.addEventListener('click', () => {
                const panelEl =
                    previousButton.closest<HTMLElement>('[data-wizard-panel]');
                if (panelEl?.dataset.wizardPanelSlug === 'consent') {
                    this.handleConsentPanelPreviousButton();
                    return;
                }
                if (panelEl && this.isSubPanel('visible')) {
                    this.setSubPanelVisibility('hidden');
                    this.#potentiallyHideNextButton(panelEl);
                    if (panelEl?.dataset.wizardPanelStep === '0')
                        previousButton.classList.add('gb-hide');
                    return;
                }

                const newStep = this.#getNewStep(this.#currentStep, 'previous');
                this.#setCurrentStep(newStep);
            });
        });

        const restartButtons = this.#wizard.querySelectorAll('[data-wizard-go-restart]');
        restartButtons.forEach(restartButton => {
            restartButton.addEventListener('click', () => {
                const languageSelectorPanels = this.#wizard.querySelectorAll(
                    '[data-wizard-panel-is-language-selector]'
                );
                if (languageSelectorPanels.length === 1) {
                    const firstTwoStepVariations = this.#wizardStepVariations.slice(0, 2);
                    this.#wizardStepVariations = firstTwoStepVariations;
                    this.#setCurrentStep(1);
                } else {
                    this.#wizardStepVariations = ['default'];
                    this.#setCurrentStep(0);
                }
            });
        });
        const selectionButtons: (HTMLButtonElement | HTMLAnchorElement)[] = Array.from(
            this.#wizard.querySelectorAll('[data-wizard-selection-button]')
        );
        // A "selection button" is a button that can be selected in the grid, such as  a business type or product
        selectionButtons.forEach(selectionButton => {
            selectionButton.addEventListener('click', e => {
                this.#handleSettingSelectionButtons(e);
                // techdebt from MW-10170, to be removed or integrated after SSBO TTE test is complete
                if ('wizardSsboOption' in selectionButton.dataset) {
                    handleSSBORedirectClick(e);
                }
                if (
                    !('wizardFormAdpField' in selectionButton.dataset) &&
                    selectionButton instanceof HTMLButtonElement
                ) {
                    const variation = selectionButton.dataset.wizardSetStepVariation;
                    this.#handleStepDisabling(selectionButton);
                    const nextStep = this.#getNewStep(this.#currentStep, 'next');
                    this.#wizardStepVariations[nextStep] = variation || 'default';
                    this.#setCurrentStep(nextStep);
                }
            });
        });

        const subPanelButton: HTMLButtonElement | null = this.#wizard.querySelector(
            '[data-wizard-sub-panel-button]'
        );
        subPanelButton?.addEventListener('click', e => {
            if (e.currentTarget instanceof HTMLButtonElement) {
                this.#wizard.scrollIntoView(true);
                this.#handleSettingSelectionButtons(e);
                const panelEl =
                    e.currentTarget.closest<HTMLElement>('[data-wizard-panel]');
                const previousButton = panelEl?.querySelector(
                    '[data-wizard-go-previous]'
                );
                if (this.isSubPanel('hidden')) {
                    this.setSubPanelVisibility('visible');
                    if (panelEl) this.#potentiallyHideNextButton(panelEl);
                    if (panelEl?.dataset.wizardPanelStep === '0')
                        previousButton?.classList.remove('gb-hide');
                }
            }
        });
    }
}
export default function wizard(wizards: HTMLElement[]) {
    wizards.forEach(wizard => {
        const w = new Wizard(wizard);
        // 1: show wizard in current state
        w.showCurrentState(false);
        // 2: add event listeners for all buttons
        w.listenToButtons();
    });
}
